import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sparkles, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const servicesList = [
    { name: 'Web Development', path: '/services/web-development' },
    { name: 'Mobile App Development', path: '/services/mobile-app' },
    { name: 'UI/UX Design', path: '/services/ui-ux-design' },
    { name: 'Digital Marketing', path: '/services/digital-marketing' },
    { name: 'E-Commerce Development', path: '/services/e-commerce' },
    { name: 'AI & ML Solutions', path: '/services/ai-ml' },
    { name: 'Data Analytics', path: '/services/data-analytics' },
    { name: 'Cloud Solutions', path: '/services/cloud-solutions' },
    { name: 'Blockchain Development', path: '/services/blockchain' },
    { name: 'Creative Copywriting', path: '/services/creative-copywriting' }
  ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services', dropdown: true },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => {
    if (path === '/services') {
      return location.pathname === '/services' || location.pathname.startsWith('/services/');
    }
    return location.pathname === path;
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-black/95 backdrop-blur-xl border-b border-white/10 shadow-2xl'
            : 'bg-black/80 backdrop-blur-md'
        }`}
      >
        <div className="max-w-8xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="group flex items-center">
              <img 
                src="/syncore-logo.png" 
                alt="SYNCORE LLC" 
                className="h-8 md:h-10 w-auto transition-opacity group-hover:opacity-80"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div
                    key={link.name}
                    className="relative group"
                    onMouseEnter={() => setShowServices(true)}
                    onMouseLeave={() => setShowServices(false)}
                  >
                    <Link
                      to={link.path}
                      className={`px-5 py-2 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${
                        isActive(link.path)
                          ? 'text-light-pink bg-white/10'
                          : 'text-white hover:text-light-pink hover:bg-white/5'
                      }`}
                    >
                      {link.name}
                    </Link>

                    <AnimatePresence>
                      {showServices && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-80 bg-black/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl py-3 overflow-hidden"
                        >
                          <div className="px-4 py-2 border-b border-white/10 mb-2">
                            <p className="text-xs font-semibold text-light-pink uppercase tracking-wider">
                              Our Services
                            </p>
                          </div>
                          <div className="max-h-96 overflow-y-auto">
                            {servicesList.map((service, index) => (
                              <Link
                                key={index}
                                to={service.path}
                                className="block px-4 py-3 text-sm text-white hover:bg-white/10 hover:text-light-pink transition-all group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-1.5 h-1.5 rounded-full bg-light-pink opacity-0 group-hover:opacity-100 transition-opacity" />
                                  <span>{service.name}</span>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-5 py-2 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${
                      isActive(link.path)
                        ? 'text-light-pink bg-white/10'
                        : 'text-white hover:text-light-pink hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* Cart Icon */}
            <div className="hidden lg:block">
              <Link to="/cart" className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all relative"
                >
                  <ShoppingCart size={20} />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-light-pink to-mid-purple text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {getCartCount()}
                    </span>
                  )}
                </motion.button>
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-light-pink to-mid-purple text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:shadow-2xl transition-all"
                >
                  Get Started
                </motion.button>
              </Link>
            </div>

            {/* Mobile Cart & Menu Icons */}
            <div className="lg:hidden flex items-center gap-3">
              <Link to="/cart" className="relative">
                <button className="p-2 rounded-xl text-white hover:bg-white/10 transition-colors relative">
                  <ShoppingCart size={20} />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-light-pink to-mid-purple text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {getCartCount()}
                    </span>
                  )}
                </button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-xl text-white hover:bg-white/10 transition-colors"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-black/98 backdrop-blur-xl pt-24"
          >
            <div className="px-6 py-8 space-y-6 overflow-y-auto max-h-screen">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => !link.dropdown && setIsOpen(false)}
                    className={`block text-2xl font-bold py-2 transition-colors ${
                      isActive(link.path) ? 'text-light-pink' : 'text-white hover:text-light-pink'
                    }`}
                  >
                    {link.name}
                  </Link>
                  
                  {link.dropdown && (
                    <div className="pl-6 mt-4 space-y-3">
                      {servicesList.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          onClick={() => setIsOpen(false)}
                          className="block text-base text-white/70 hover:text-light-pink transition-colors"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  className="w-full mt-8 px-8 py-4 rounded-xl bg-gradient-to-r from-light-pink to-mid-purple text-white font-bold text-base uppercase tracking-wider shadow-lg"
                >
                  Get Started
                </motion.button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
